import React, { useState, useEffect, useMemo } from "react";
import {
  Title,
  Button,
  Modal,
  LoadingOverlay,
  Group,
  Stack,
  Paper,
  Collapse,
  Grid,
  Checkbox,
  Divider,
  ColorSwatch,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconPlus, IconEdit, IconInfoCircle } from "@tabler/icons-react";
import ServiceFormWizard from "../forms/ServiceFormWizard";
import SanitizedServiceDescription from "../common/SanitizedServiceDescription";
import "./ServicesManagementSettings.css";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import StaffServiceDetails from "./StaffServiceDetails";
import { calculateTotalDuration } from "../../utils/timeUtils";
import usePermissions from "../../hooks/usePermissions";
import {
  filterServicesByScope,
  getEffectiveScope,
} from "../../utils/permissionFilters";

function ServicesMenuTab({ onSubmit, canCRUD = true }) {
  const { deleteSalonService } = useApp();
  const { employees, serviceTypeOptions, services, setServices, currentUser } =
    useSalon();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [expandedService, setExpandedService] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [dontAskAgain, setDontAskAgain] = useState(false);

  // Get services scope for UI filtering
  const perms = usePermissions();
  const servicesScope = getEffectiveScope(perms?.settingsServices?.scope);
  const currentUserId = currentUser?.id || "";

  // Filter services by scope permission (UI-only filtering)
  const scopeFilteredServices = useMemo(() => {
    return filterServicesByScope(services, {
      currentUserId,
      scope: servicesScope,
    });
  }, [services, currentUserId, servicesScope]);

  useEffect(() => {
    if (services && employees && serviceTypeOptions) {
      setIsLoading(false);
    }
    // Check if user has previously set "don't ask again"
    const skipConfirmation =
      localStorage.getItem("skipServiceDeleteConfirmation") === "true";
    setDontAskAgain(skipConfirmation);
  }, [services, employees, serviceTypeOptions]);

  const handleAddService = (serviceData) => {
    const newServices = [
      ...services,
      {
        id: services.length + 1,
        ...serviceData,
      },
    ];
    setServices(newServices);
    setIsFormOpen(false);
    onSubmit();
  };

  const handleEditService = (serviceData) => {
    setServices(
      services.map((service) =>
        service.id === selectedService.id
          ? { ...service, ...serviceData }
          : service,
      ),
    );
    setIsEditFormOpen(false);
    setSelectedService(null);
    onSubmit();
  };

  const handleServiceClick = (service) => {
    setExpandedService(expandedService?.id === service.id ? null : service);
  };

  const handleEditClick = (e, service) => {
    e.stopPropagation();
    setSelectedService(service);
    setIsEditFormOpen(true);
  };

  const handleDeleteClick = (e, service) => {
    e.stopPropagation();
    const skipConfirmation =
      localStorage.getItem("skipServiceDeleteConfirmation") === "true";

    if (skipConfirmation) {
      handleDeleteService(service.id);
    } else {
      setServiceToDelete(service);
      setIsDeleteModalOpen(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (dontAskAgain) {
      localStorage.setItem("skipServiceDeleteConfirmation", "true");
    }
    handleDeleteService(serviceToDelete.id);
    setIsDeleteModalOpen(false);
    setServiceToDelete(null);
  };

  const handleDeleteService = async (id) => {
    setIsLoading(true);
    const result = await deleteSalonService(id);
    if (result) {
      const remainingServices = services.filter((service) => service.id !== id);
      setServices(remainingServices);
    }
    setIsLoading(false);
  };

  // Add this helper function to group services
  const groupServicesByType = (services) => {
    if (!services) {
      return {};
    }

    return services.reduce((groups, service) => {
      const type = service.service_type || "Other";
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(service);
      return groups;
    }, {});
  };

  return (
    <>
      <LoadingOverlay visible={isLoading} />
      <Stack p="lg">
        <Group justify="space-between">
          <Group gap="xs">
            <Title order={3}>Services</Title>
            {servicesScope === "own" && (
              <Tooltip
                label="You can only view services assigned to you. Contact a manager to view all services."
                multiline
                w={280}
                withArrow
              >
                <IconInfoCircle
                  size={20}
                  style={{
                    color: "var(--mantine-color-blue-6)",
                    cursor: "help",
                  }}
                />
              </Tooltip>
            )}
          </Group>
          {canCRUD && (
            <Button
              leftSection={<IconPlus size="1rem" />}
              color="teal"
              onClick={() => setIsFormOpen(true)}
            >
              Add Service
            </Button>
          )}
        </Group>

        <Stack spacing="xl">
          {Object.entries(groupServicesByType(scopeFilteredServices)).map(
            ([type, typeServices]) => (
              <Stack key={type} spacing="xs">
                <Text fw={500} size="lg" c="dimmed">
                  {type}
                </Text>
                {typeServices?.map((service) => (
                  <Paper
                    key={service.id}
                    withBorder
                    className={`service-item ${expandedService?.id === service.id ? "selected" : ""}`}
                    onClick={() => handleServiceClick(service)}
                    radius="md"
                  >
                    <Group justify="space-between" p="md">
                      <Group gap="md">
                        <Text fw={500}>{service.name}</Text>
                      </Group>
                      <Group>
                        {canCRUD && (
                          <>
                            <Button
                              variant="light"
                              color="teal"
                              size="xs"
                              leftSection={<IconEdit size="1rem" />}
                              onClick={(e) => handleEditClick(e, service)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="subtle"
                              color="red"
                              size="xs"
                              onClick={(e) => handleDeleteClick(e, service)}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </Group>
                    </Group>

                    <Collapse
                      in={expandedService?.id === service.id}
                      className="service-item"
                    >
                      <Divider mx="md" />
                      <Stack spacing="xs" p="md">
                        <Grid>
                          <Grid.Col span={6}>
                            <Stack gap={0}>
                              <Text fw={500} size="sm">
                                Service Type
                              </Text>
                              <Text size="sm">{service.service_type}</Text>
                            </Stack>
                          </Grid.Col>
                          <Grid.Col span={6}>
                            <Stack gap={0}>
                              <Text fw={500} size="sm">
                                Default Calendar Color
                              </Text>
                              <Group gap="xs">
                                <ColorSwatch
                                  color={service.default_calendar_event_color}
                                  size={16}
                                  radius="sm"
                                />
                              </Group>
                            </Stack>
                          </Grid.Col>
                          <Grid.Col span={6}>
                            <Stack gap={0}>
                              <Text fw={500} size="sm">
                                Default Duration
                              </Text>
                              <Text size="sm">
                                {calculateTotalDuration(service)} minutes
                              </Text>
                            </Stack>
                          </Grid.Col>
                          <Grid.Col span={6}>
                            <Stack gap={0}>
                              <Text fw={500} size="sm">
                                Default Price
                              </Text>
                              <Text size="sm">
                                ${service.default_price}
                                {service.default_is_variable_price
                                  ? " and up"
                                  : ""}
                              </Text>
                            </Stack>
                          </Grid.Col>
                          <Grid.Col span={6}>
                            <Stack gap={0}>
                              <Text fw={500} size="sm">
                                Description
                              </Text>
                              {service.description ? (
                                <SanitizedServiceDescription
                                  html={service.description}
                                />
                              ) : (
                                <Text size="sm" c="dimmed">
                                  No description provided
                                </Text>
                              )}
                            </Stack>
                          </Grid.Col>
                          <Grid.Col span={6}>
                            <Stack gap={0}>
                              <Text fw={500} size="sm">
                                Show in online booking
                              </Text>
                              <Text size="sm">
                                {service.default_is_self_servicable
                                  ? "Yes"
                                  : "No"}
                              </Text>
                            </Stack>
                          </Grid.Col>
                          <Grid.Col span={6}>
                            <Stack gap={0}>
                              <Text fw={500} size="sm">
                                Require card on file to book
                              </Text>
                              <Text size="sm">
                                {service.require_card_to_book ? "Yes" : "No"}
                              </Text>
                            </Stack>
                          </Grid.Col>
                        </Grid>

                        <Stack gap={0} mt="md">
                          <Text fw={500} size="sm">
                            Staff Availability
                          </Text>
                          {employees?.map((employee) => (
                            <StaffServiceDetails
                              key={employee.id}
                              employee={employee}
                              service={service}
                              staffService={service.servicing_staff?.find(
                                (ss) => ss.staff_id === employee.id,
                              )}
                            />
                          ))}
                        </Stack>
                      </Stack>
                    </Collapse>
                  </Paper>
                ))}
              </Stack>
            ),
          )}
        </Stack>

        <ServiceFormWizard
          opened={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSuccess={handleAddService}
          serviceTypes={serviceTypeOptions}
        />

        <ServiceFormWizard
          opened={isEditFormOpen}
          onClose={() => {
            setIsEditFormOpen(false);
            setSelectedService(null);
          }}
          onSuccess={handleEditService}
          initialData={selectedService}
          isEditing={true}
          serviceTypes={serviceTypeOptions}
        />

        <Modal
          opened={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setServiceToDelete(null);
          }}
          title="Confirm Delete"
          size="sm"
        >
          <Stack>
            <Stack gap="xs">
              <Text>Delete this service?</Text>
              <Text>
                It will be hidden from your portal and the customer booking
                site. Past appointments and reports will keep showing it.
              </Text>
            </Stack>
            <Checkbox
              label="Don't ask me this again"
              checked={dontAskAgain}
              onChange={(event) => setDontAskAgain(event.currentTarget.checked)}
            />
            <Group w="100%" justify="space-between">
              <Button
                variant="light"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setServiceToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button color="red" onClick={handleDeleteConfirm}>
                Delete
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </>
  );
}

export default ServicesMenuTab;
